import { Badge } from '@/components/ui/badge';

import { usePricingReadiness } from '../api/use-pricing-readiness';

import type { InvoiceDirection } from '../api/use-applied-provisional';

// Data-driven pricing-state chip for a BL side. Final means BOTH the side's
// price and its weight are fixed — never the chain position or the document
// type the operator picked. A chain can therefore read Final from its very
// first document when both values were already known at creation. The label
// carries an explicit "Pricing:" prefix so it can't be mistaken for the
// document type it sits next to (a draft Provisional on a fully-fixed side).
export function PricingStateBadge({
  blOrderId,
  direction,
}: Readonly<{ blOrderId: string | null | undefined; direction: InvoiceDirection }>) {
  const readiness = usePricingReadiness(blOrderId ?? undefined, direction);
  if (readiness.data === undefined) return null;
  const priceFixed = readiness.data.price_fixed;
  const weightFixed = readiness.data.weight_fixed;
  const isFinal = priceFixed && weightFixed;
  const detail = `Price ${priceFixed ? 'fixed' : 'not fixed'} · weight ${weightFixed ? 'fixed' : 'not fixed'}.`;
  const title = isFinal
    ? `Pricing is final — both the price and the weight are fixed for this side. ${detail}`
    : `Pricing is provisional — the side stays provisional until both the price and the weight are fixed. ${detail}`;
  return (
    <Badge
      variant={isFinal ? 'secondary' : 'outline'}
      title={title}
      className={
        isFinal
          ? 'border-purple-200 bg-purple-100 text-purple-700 hover:bg-purple-100 dark:border-purple-900/50 dark:bg-purple-950/50 dark:text-purple-300'
          : 'text-muted-foreground font-normal'
      }
      data-testid={`pricing-state-${direction}`}
    >
      {isFinal ? 'Pricing: Final' : 'Pricing: Provisional'}
    </Badge>
  );
}
